import React, { useState, useEffect, useCallback } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api from "@/lib/api";
import { FillLibrary } from "@/components/FillLibrary";
import { ClipPack } from "@/components/ClipPack";
import { MyPacks } from "@/components/MyPacks";
import { useAuth } from "@/context/AuthContext";
import { makeWelcomeFlyer } from "@/lib/flyer";
import { readCaption } from "@/lib/useCaption";
import { toast } from "sonner";
import { Star, Trash2, Play, Clock, History, Music, Package, FileText } from "lucide-react";

export default function LibrarySheet({ open, onOpenChange, onLoadTab, refreshKey, highlightId, onChanged }) {
  const [filter, setFilter] = useState("all");
  const [saved, setSaved] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const highlightRef = React.useRef(null);
  const { user } = useAuth();
  const [packOpen, setPackOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, h] = await Promise.all([api.get("/tabs"), api.get("/history")]);
      setSaved(s.data);
      setHistory(h.data);
    } catch (e) {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, refreshKey, load]);

  useEffect(() => {
    if (open && highlightId) {
      setFilter("all");
      const t = setTimeout(() => highlightRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 250);
      return () => clearTimeout(t);
    }
  }, [open, highlightId, saved]);

  const toggleFav = async (id) => {
    try {
      await api.patch(`/tabs/${id}/favorite`);
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/tabs/${id}`);
      load();
      onChanged && onChanged();
      toast.success("Removed");
    } catch (e) {
      toast.error("Failed");
    }
  };

  const clearHistory = async () => {
    try {
      await api.delete("/history");
      setHistory([]);
      toast.success("History cleared");
    } catch (e) {
      toast.error("Failed");
    }
  };

  const openTab = (tab) => {
    onLoadTab(tab);
    onOpenChange(false);
  };

  const list = filter === "favorites" ? saved.filter((t) => t.is_favorite) : saved;

  const TabCard = ({ item, isHistory }) => {
    const isHL = !isHistory && highlightId && item.id === highlightId;
    return (
    <div
      ref={isHL ? highlightRef : null}
      className={`rounded-xl bg-white/[0.03] border p-4 transition-colors ${isHL ? "border-[#ffab00] ring-1 ring-[#ffab00]/50 shadow-[0_0_20px_rgba(255,171,0,0.25)]" : "border-white/10 hover:border-[#00f0ff]/30"}`}
      data-testid={`saved-tab-card-${item.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold truncate">{item.tab.title}</p>
          <p className="text-sm text-slate-500 truncate">{item.tab.artist}</p>
          <div className="flex gap-1.5 mt-2">
            {isHL && <Badge className="bg-[#ffab00]/20 text-[#ffab00] border-0 text-[10px] font-tab" data-testid="just-saved-badge">Just saved</Badge>}
            <Badge className="bg-[#00f0ff]/15 text-[#00f0ff] border-0 text-[10px] font-tab">{item.tab.bpm} BPM</Badge>
            <Badge className="bg-white/5 text-slate-400 border-0 text-[10px] font-tab">{item.tab.difficulty}</Badge>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-3">
        <Button
          size="sm" onClick={() => openTab(isHistory ? item.tab : { ...item.tab, _libId: item.id })}
          className="flex-1 bg-[#00f0ff] text-black hover:bg-[#33f4ff] h-8"
          data-testid={`load-tab-${item.id}`}
        >
          <Play className="w-3.5 h-3.5 mr-1" /> Open
        </Button>
        {!isHistory && (
          <>
            <Button
              size="icon" variant="outline"
              onClick={() => toggleFav(item.id)}
              className={`h-8 w-8 border-white/15 ${item.is_favorite ? "text-[#ffab00]" : "text-slate-400"}`}
              data-testid={`fav-${item.id}`}
            >
              <Star className="w-3.5 h-3.5" fill={item.is_favorite ? "#ffab00" : "none"} />
            </Button>
            <Button
              size="icon" variant="outline"
              onClick={() => del(item.id)}
              className="h-8 w-8 border-white/15 text-[#FF3366]"
              data-testid={`delete-${item.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </>
        )}
      </div>
    </div>
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="bg-[#0b0c10] border-white/10 w-full sm:max-w-lg overflow-y-auto scroll-thin">
        <SheetHeader>
          <SheetTitle className="font-heading text-3xl uppercase tracking-tight text-left">
            My <span className="amber-text">Library</span>
          </SheetTitle>
        </SheetHeader>

        <Tabs value={filter} onValueChange={setFilter} className="mt-6">
          <TabsList className="grid grid-cols-5 w-full bg-white/5">
            <TabsTrigger value="all" data-testid="library-filter-all">All</TabsTrigger>
            <TabsTrigger value="favorites" data-testid="library-filter-favorites">Favorites</TabsTrigger>
            <TabsTrigger value="fills" data-testid="library-filter-fills">Fills</TabsTrigger>
            <TabsTrigger value="packs" data-testid="library-filter-packs">Packs</TabsTrigger>
            <TabsTrigger value="history" data-testid="library-filter-history">History</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="mt-5 space-y-3">
          {loading && <p className="text-sm text-slate-500 text-center py-8">Loading…</p>}

          {!loading && filter === "fills" && <FillLibrary saved={saved} onPractice={openTab} />}
          {filter === "packs" && <MyPacks />}

          {!loading && (filter === "all" || filter === "favorites") && saved.length > 0 && (
            <button
              onClick={() => (user?.is_pro ? setPackOpen(true) : toast.error("Clip packs are a Pro feature"))}
              className="w-full inline-flex items-center justify-center gap-2 h-9 rounded-full border border-[#ffab00]/40 text-[#ffab00] bg-[#ffab00]/5 hover:bg-[#ffab00]/15 text-xs font-tab uppercase tracking-widest transition-colors"
              data-testid="clip-pack-button"
            >
              <Package className="w-4 h-4" /> Student clip pack
            </button>
          )}
          {!loading && (filter === "all" || filter === "favorites") && user && (
            <button
              onClick={() => makeWelcomeFlyer({ caption: readCaption(user).trim(), teacher: user.name, referralCode: user.referral_code })
                .then(() => toast.success("Welcome flyer downloaded"))
                .catch(() => toast.error("Could not make the flyer"))}
              className="w-full inline-flex items-center justify-center gap-2 h-9 rounded-full border border-[#00f0ff]/40 text-[#00f0ff] bg-[#00f0ff]/5 hover:bg-[#00f0ff]/15 text-xs font-tab uppercase tracking-widest transition-colors"
              data-testid="welcome-flyer-button"
            >
              <FileText className="w-4 h-4" /> Welcome flyer for new students (PDF)
            </button>
          )}
          {packOpen && <ClipPack saved={list} onClose={() => setPackOpen(false)} />}

          {!loading && filter !== "history" && filter !== "fills" && filter !== "packs" && list.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <Music className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No saved tabs yet. Generate one and hit save.</p>
            </div>
          )}

          {!loading && filter !== "history" && filter !== "fills" && filter !== "packs" &&
            list.map((item) => <TabCard key={item.id} item={item} />)}

          {!loading && filter === "history" && (
            <>
              {history.length > 0 && (
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-slate-500 font-tab uppercase tracking-widest flex items-center gap-1">
                    <History className="w-3.5 h-3.5" /> Recent generations
                  </span>
                  <Button variant="ghost" size="sm" onClick={clearHistory}
                    className="text-[#FF3366] h-7 text-xs" data-testid="clear-history-button">
                    Clear
                  </Button>
                </div>
              )}
              {history.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Clock className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p className="text-sm">No generation history yet.</p>
                </div>
              ) : (
                history.map((item) => <TabCard key={item.id} item={item} isHistory />)
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
